import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import className from 'classnames';
import React, {Dispatch, useState} from 'react';
import isEqual from 'react-fast-compare';

import ProgramParamsSection from 'analyses/alignment/modals/ProgramParamsSection';
import {
  BASE_DETAIL_TEXT,
  BASE_TEXT,
  BASIC_MARGIN,
  BASIC_VERTICAL_MARGIN,
  CLICKABLE,
  COL_PADDING_OFFSET,
  FULL_WIDTH_MODAL_RULE,
  ICON_COLOR,
  MAX_WIDTH_100,
  WITHOUT_BUTTON_FOCUS_STYLING,
} from 'analyses/alignment/modals/StyleConstants';
import BenchlingConfig from 'BenchlingConfig';
import {
  AlignmentProgram,
  AlignmentProgramStrategy,
  BulkAlignmentCreationKind,
  CLUSTALO_DEFAULTS,
  CLUSTALO_PARAMETER_OPTION_VALUES,
  ClustalOParameters,
  MAFFT_DEFAULTS,
  MAFFT_PARAMETER_OPTION_VALUES,
  MafftParameters,
  MafftStrategy,
} from 'constants/AlignmentConstants';
import FolderSelector from 'file-browser/FolderSelector';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {spacingBottom} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import InformationalBanner from 'ui-library/banners/InformationalBanner';
import InlineBanner, {
  InlineBannerActionType,
  InlineBannerProps,
  InlineBannerType,
} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT, GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import MenuPopover from 'ui-library/menus/MenuPopover';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import ToggleGroup from 'ui-library/ToggleGroup';
import {ElementMouseEvent, Optional} from 'util/Types';

import {AlignmentInputSelector} from './AlignmentInputSelector';
import {
  AlignmentInput,
  ConsensusSetup,
  CreateSequenceAlignmentModalAction,
  CreateSequenceAlignmentModalActionType,
  CreateSequenceAlignmentModalState,
  InputType,
  TemplateSetup,
} from './createSequenceAlignmentsModalReducer';
import {
  getInputChip,
  getSelectedSetups,
  inputToOptionData,
  isTemplateSetup,
  requiresFolderId,
} from './SharedUtil';

function MafftParametersOptions({
  parameters,
  dispatch,
}: {
  parameters: MafftParameters;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  type MafftParametersOmitStrategy = Omit<MafftParameters, 'strategy'>;
  const parameterNames = [
    'maxIterations',
    'retree',
    'gapOpenPenalty',
    'gapExtensionPenalty',
    'adjustDirection',
  ].filter(
    (name: keyof MafftParametersOmitStrategy) =>
      !MAFFT_PARAMETER_OPTION_VALUES[name].irrelevantStrategies?.includes(parameters.strategy)
  );
  const updateParams = (params: {[param in keyof MafftParameters]: MafftParameters[param]}): void => {
    dispatch({
      type: CreateSequenceAlignmentModalActionType.UPDATE_MAFFT_PARAMETERS,
      parameters: params,
    });
  };
  return (
    <ProgramParamsSection
      currentParameters={parameters as MafftParametersOmitStrategy}
      parameterNames={parameterNames as Array<keyof MafftParametersOmitStrategy>}
      parameterOptionValues={MAFFT_PARAMETER_OPTION_VALUES}
      defaultParameters={MAFFT_DEFAULTS}
      updateParams={updateParams}
    />
  );
}

function ClustalOParametersOptions({
  parameters,
  dispatch,
}: {
  parameters: ClustalOParameters;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  const parameterNames: Array<keyof ClustalOParameters> = [
    'mbedGuideTree',
    'mbedIteration',
    'numCombinedIterations',
    'maxGuidetreeIterations',
    'maxHmmIterations',
  ];
  const updateParams = (params: {[param in keyof ClustalOParameters]: ClustalOParameters[param]}): void => {
    dispatch({
      type: CreateSequenceAlignmentModalActionType.UPDATE_CLUSTALO_PARAMETERS,
      parameters: params,
    });
  };
  return (
    <ProgramParamsSection
      currentParameters={parameters}
      parameterNames={parameterNames}
      parameterOptionValues={CLUSTALO_PARAMETER_OPTION_VALUES}
      defaultParameters={CLUSTALO_DEFAULTS}
      updateParams={updateParams}
    />
  );
}

function FolderSection({
  folderId,
  setFolderId,
}: {
  folderId: Optional<string>;
  setFolderId: (folderId: string) => void;
}): JSX.Element {
  return (
    <>
      <div className={css(styles.text, styles.basicVerticalMargin)}>Select destination folder.</div>
      <FolderSelector
        enableRedesign={true}
        showInventory={true}
        value={folderId}
        onChange={(e: {target: {value: string}}) => {
          setFolderId(e.target.value);
        }}
        unsetOnFetchFail={false}
      />
    </>
  );
}

/**
 * Renders a clickable "X" button.
 * @param onClick Click event handler.
 * @constructor
 */
function CrossTickButton({onClick}: {onClick: (e: ElementMouseEvent) => void}): JSX.Element {
  return (
    <div className={css(styles.clickable, styles.crossIconContainer)} onClick={onClick}>
      <BenchlingIcon className={css(styles.crossIcon)} icon="cross-thick" />
    </div>
  );
}

/**
 * Renders a "+" icon with a dropdown menu for adding a template group.
 * @param inputs Alignment inputs to include in the menu.
 * @param templateGroups The current template groups.
 * @param allowDuplicateTemplates Whether to allow multiple groups with the same template.
 * @param addGroup Callback for adding a new template group.
 * @param onClose Callback for when the add template group dropdown is closed.
 * @constructor
 */
export function AddTemplateGroupDropdown({
  inputs,
  templateGroups,
  allowDuplicateTemplates,
  addGroup,
  onClose,
}: {
  inputs: Array<AlignmentInput>;
  templateGroups: Array<TemplateSetup>;
  allowDuplicateTemplates: boolean;
  addGroup: (template: AlignmentInput) => void;
  onClose: () => void;
}): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  function getDisabledOptionProps(input: AlignmentInput): {disabled?: boolean; disabledMessage?: string} {
    if (input.type === InputType.BIOENTITY && !input.canBeTemplate) {
      return {disabled: true, disabledMessage: 'Lacking edit permissions.'};
    } else if (!allowDuplicateTemplates && templateGroups.some((config) => isEqual(config.template, input))) {
      return {disabled: true, disabledMessage: 'Input already used as template.'};
    } else {
      return {};
    }
  }

  const onToggleDropdown = (isDropdownOpen: boolean): void => {
    if (!isDropdownOpen) {
      onClose();
    }
    setIsDropdownOpen(isDropdownOpen);
  };

  const menu = (
    <MenuPopover>
      <SearchableMenu
        optionDatas={inputs.map((input) => inputToOptionData(input, getDisabledOptionProps))}
        onSelect={addGroup}
        scrollable={true}
      />
    </MenuPopover>
  );

  return (
    <Dropdown
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      alwaysDropDown={true}
      menu={menu}
      isOpen={isDropdownOpen}
      onToggle={onToggleDropdown}
    >
      <span
        className={classNames(css(styles.plusIcon, styles.clickable, styles.alignBottom))}
        {...getTestProps({component: 'CreateSequenceAlignmentsModal', element: 'AddTemplateGroupIcon'})}
      >
        <BenchlingIcon icon="compose" />
      </span>
    </Dropdown>
  );
}

/**
 * Renders a form for configuring templates groups for an alignment
 * @param addGroup Callback for adding a new group.
 * @param circularTemplateRotationDisabled Whether to disable circular template rotation.
 * @param setDisableCircularTemplateRotation Callback for setting the disable circular template rotation status.
 * @param removeGroup Callback for removing an existing group.
 * @param onClose Callback for when the add group dropdown is closed.
 * @param allowMulti Whether to allow multiple groups.
 * @param addInput Callback for adding an input item to a group.
 * @param removeInput Callback for removing an input item from a group.
 * @param templateGroups The current set of template groups.
 * @param allowDuplicateTemplates Whether to allow multiple groups for the same template.
 * @param inputs Inputs to choose from when adding items to a group or selecting a template.
 * @param alignmentKind The approach to arranging the sequences against each other
 * @constructor
 */
export function TemplateAlignmentConfiguration({
  addGroup,
  circularTemplateRotationDisabled,
  removeGroup,
  onClose,
  allowMulti,
  addInput,
  removeInput,
  templateGroups,
  setDisableCircularTemplateRotation,
  allowDuplicateTemplates,
  inputs,
  alignmentKind,
}: {
  addGroup: (template: AlignmentInput) => void;
  removeGroup: (index: number) => void;
  onClose: () => void;
  allowMulti: boolean;
  addInput: (index: number, input: AlignmentInput) => void;
  removeInput: (index: number, input: AlignmentInput) => void;
  templateGroups: Array<TemplateSetup>;
  setDisableCircularTemplateRotation: (value: boolean) => void;
  allowDuplicateTemplates: boolean;
  inputs: Array<AlignmentInput>;
  alignmentKind: BulkAlignmentCreationKind;
  circularTemplateRotationDisabled: boolean;
}): JSX.Element {
  return (
    <>
      <div className={classNames(css(styles.colPaddingOffset, styles.flex))}>
        <div className={classNames(css(styles.alignmentRows))}>
          <div>
            <Col lg={3} md={3} sm={3} xs={3}>
              <div>
                <span className={css(styles.text)}>Template(s)</span>
                {allowMulti && (
                  <AddTemplateGroupDropdown
                    inputs={inputs}
                    templateGroups={templateGroups}
                    allowDuplicateTemplates={allowDuplicateTemplates}
                    addGroup={addGroup}
                    onClose={onClose}
                  />
                )}
              </div>
            </Col>
            <Col lg={9} md={9} sm={9} xs={9}>
              <span className={css(styles.text)}>Non-template sequence(s)</span>
            </Col>
          </div>
          {templateGroups.map(({template, items}, i) => (
            <div key={i} className={classNames(css(styles.flex))}>
              <Col lg={3} md={3} sm={3} xs={3} className={classNames(css(styles.flex))}>
                {getInputChip(template, {size: ChipSize.SMALL, onRemove: undefined}, styles.inputChip)}
              </Col>
              <Col lg={8} md={8} sm={8} xs={8}>
                <AlignmentInputSelector
                  autofocus={false}
                  choiceValues={inputs}
                  getDisabledOptionProps={(input) =>
                    isEqual(input, template)
                      ? {disabled: true, disabledMessage: 'Cannot align sequence against itself.'}
                      : {}
                  }
                  selection={items}
                  onSelect={(input: AlignmentInput) => {
                    if (items.some((other) => isEqual(input, other))) {
                      removeInput(i, input);
                    } else {
                      addInput(i, input);
                    }
                  }}
                  alignmentKind={alignmentKind}
                />
              </Col>
              <Col lg={1} md={1} sm={1} xs={1} className={classNames(css(styles.flex))}>
                {allowMulti && (
                  <CrossTickButton
                    onClick={() => {
                      removeGroup(i);
                    }}
                  />
                )}
              </Col>
            </div>
          ))}
        </div>
      </div>
      {BenchlingConfig.MOLBIO_ENABLE_CIRCULAR_ALIGNMENT_ROTATION_OPT_OUT && (
        <>
          <hr />
          <div>
            <CheckboxInput
              id="disable-circular-template-rotation"
              label="Disable circular template rotation"
              checked={circularTemplateRotationDisabled}
              onChange={(e) => {
                setDisableCircularTemplateRotation(e.target.checked);
              }}
              helpText="When enabled, prevents the alignment algorithm from trying to rotate the circular template sequence or other inputs. It has no effect when the template is linear but can be useful to avoid timeouts for complex alignments."
            />
          </div>
        </>
      )}
    </>
  );
}

/**
 * Renders a form for configuring consensus groups for an alignment
 * @param addGroup Callback for adding a new group.
 * @param removeGroup Callback for removing an existing group.
 * @param renameGroup Callback for renaming a group.
 * @param allowMulti Whether to allow multiple groups.
 * @param disableRename Whether to allow renaming of the alignment groups.
 * @param addInput Callback for adding an input item to a group.
 * @param removeInput Callback for removing an input item from a group.
 * @param consensusGroups The current set of consensus groups.
 * @param inputs Inputs to choose from when adding items to a group.
 * @constructor
 */
export function ConsensusAlignmentConfiguration({
  addGroup,
  removeGroup,
  allowMulti,
  disableRename,
  renameGroup,
  consensusGroups,
  inputs,
  addInput,
  removeInput,
}: {
  addGroup: () => void;
  removeGroup: (index: number) => void;
  allowMulti: boolean;
  disableRename: boolean;
  renameGroup: (index: number, name: string) => void;
  consensusGroups: Array<ConsensusSetup>;
  inputs: Array<AlignmentInput>;
  addInput: (index: number, input: AlignmentInput) => void;
  removeInput: (index: number, input: AlignmentInput) => void;
}): JSX.Element {
  return (
    <div className={classNames(css(styles.colPaddingOffset, styles.flex))}>
      <div className={classNames(css(styles.alignmentRows))}>
        <Col lg={12} md={12} sm={12} xs={12}>
          <div className={css(styles.headerWrapper)}>
            <span className={css(styles.text)}>Group(s)</span>
            {allowMulti && (
              <span
                onClick={() => {
                  addGroup();
                }}
                className={classNames(css(styles.plusIcon, styles.clickable, styles.alignBottom))}
              >
                <BenchlingIcon icon="compose" />
              </span>
            )}
          </div>
        </Col>
        {consensusGroups.map(({name, items, nucleotideType}, i) => (
          <div className={css(styles.headerWrapper)} key={i}>
            <Col lg={3} md={3} sm={3} xs={3}>
              <TextField
                onChange={(e) => {
                  renameGroup(i, e.target.value);
                }}
                value={name}
                placeholder="Alignment name"
                label=""
                labelStyle={FieldLabel.STYLES.HIDDEN}
                disabled={disableRename}
              />
            </Col>
            <Col lg={8} md={8} sm={8} xs={8}>
              <AlignmentInputSelector
                autofocus={false}
                choiceValues={inputs}
                getDisabledOptionProps={(input) => {
                  if (nucleotideType && input.nucleotideType !== nucleotideType) {
                    return {
                      disabled: true,
                      disabledText: `Only one nucleotide type can be included in each consensus alignment. This is a ${nucleotideType} alignment.`,
                    };
                  } else {
                    return {};
                  }
                }}
                selection={items}
                onSelect={(input: AlignmentInput) => {
                  if (items.some((other) => isEqual(input, other))) {
                    removeInput(i, input);
                  } else {
                    addInput(i, input);
                  }
                }}
                alignmentKind={BulkAlignmentCreationKind.CONSENSUS}
              />
            </Col>
            <Col lg={1} md={1} sm={1} xs={1}>
              {allowMulti && (
                <CrossTickButton
                  onClick={() => {
                    removeGroup(i);
                  }}
                />
              )}
            </Col>
          </div>
        ))}
      </div>
    </div>
  );
}

const ALIGNMENT_PROGRAM_DETAILS: {
  [program in AlignmentProgram]: {
    programDisplay: string;
    programDescription: string;
    programRecommendation: string;
    programReferenceLink: JSX.Element;
    strategies: Array<{value: AlignmentProgramStrategy; display: string; description: string}>;
  };
} = {
  [AlignmentProgram.MAFFT]: {
    programDisplay: 'MAFFT',
    programDescription: 'Faster, less precise, can reverse sequences',
    programRecommendation: 'recommended for nucleotide alignments',
    programReferenceLink: (
      <a href="http://mbe.oxfordjournals.org/content/30/4/772" target="_blank" rel="noopener noreferrer">
        MAFFT v7 (Katoh, Standley 2013)
      </a>
    ),
    strategies: [
      {
        value: MafftStrategy.AUTO,
        display: 'Auto',
        description: 'Pick a strategy automatically based on the count and size of inputs.',
      },
      {
        value: MafftStrategy.SIXMER,
        display: '6-mer distance',
        description: 'Use 6-mer distance for constructing the guide tree.',
      },
      {
        value: MafftStrategy.LOCALPAIR,
        display: 'Local pairwise',
        description:
          'Compute local pairwise alignments using Smith-Waterman for constructing the guide tree and iterative refinement.',
      },
      {
        value: MafftStrategy.GLOBALPAIR,
        display: 'Global pairwise',
        description:
          'Compute global pairwise alignments using Needleman-Wunsch for constructing the guide tree and iterative refinement.',
      },
    ],
  },
  [AlignmentProgram.CLUSTALO]: {
    programDisplay: 'Clustal Omega',
    programDescription: 'Slower, more precise, cannot reverse sequences',
    programRecommendation: 'recommended for amino acid alignments',
    programReferenceLink: (
      <a href="http://dx.doi.org/10.1038/msb.2011.75" target="_blank" rel="noopener noreferrer">
        Clustal Omega (Sievers et al. 2011)
      </a>
    ),
    strategies: [
      {
        value: AlignmentProgram.CLUSTALO,
        display: 'Multiple (3+) sequence alignment',
        description: 'Use seeded guide trees and HMM profile-profile techniques.',
      },
    ],
  },
};

const styles = StyleSheet.create({
  alignmentKindToggleInfoText: BASE_DETAIL_TEXT,
  text: BASE_TEXT,
  referenceText: {...FONT_PROXIMA_11, marginBottom: '-4px'},
  fullWidthHorizontalRule: FULL_WIDTH_MODAL_RULE,
  param: {marginBottom: BASIC_MARGIN},
  alignmentDiagram: MAX_WIDTH_100,
  basicVerticalMargin: BASIC_VERTICAL_MARGIN,
  colPaddingOffset: COL_PADDING_OFFSET,
  withoutButtonFocusStyling: WITHOUT_BUTTON_FOCUS_STYLING,
  plusIcon: {...ICON_COLOR, marginLeft: '8px'},
  crossIcon: {color: GRAY_DARK},
  crossIconContainer: {...CLICKABLE, alignSelf: 'center'},
  clickable: CLICKABLE,
  inputChip: {flex: 0, alignSelf: 'center'},
  headerWrapper: {display: 'flex', alignItems: 'center'},
  alignmentRows: {display: 'flex', flexDirection: 'column', flex: 1, gap: '10px'},
  flex: {display: 'flex'},
  alignBottom: {verticalAlign: 'bottom'},
  buttonGroupBottomSpacing: spacingBottom,
  input: {
    marginTop: '2px',
    verticalAlign: 'top',
  },
  programRecommendation: {
    color: GRAY_DARK,
  },
  radioButtonDescription: {
    marginLeft: '18px',
    whiteSpace: 'normal',
  },
  radioButtonLabel: {
    borderStyle: 'solid',
    borderRadius: '5px',
    padding: '10px',
    margin: '10px',
  },
  radioButtonUnselected: {
    borderColor: GRAY_LIGHT,
  },
  radioButtonSelected: {
    borderColor: BLUE_LIGHT,
  },
});

const ContigMessage = (): JSX.Element => (
  <div>
    If you’d like to perform a contig alignment (shown below), we recommend selecting the MAFFT “local
    pairwise” algorithm.
  </div>
);

const ALIGNMENT_KIND_INLINE_BANNER_PROPS: {
  [kind in BulkAlignmentCreationKind]: InlineBannerProps;
} = {
  [BulkAlignmentCreationKind.PAIRWISE]: {
    text: 'Pairwise Alignment - Make one alignment against the template for each non-template sequence.',
    supportingInfo: (
      <img className={css(styles.alignmentDiagram)} src={staticURL('/static/img/pairwise_diagram.svg')} />
    ),
    action: {
      actionType: InlineBannerActionType.ACCORDION,
    },
    bannerType: InlineBannerType.INFO,
    hideIcon: true,
  },
  [BulkAlignmentCreationKind.MULTISEQUENCE]: {
    text: 'Multisequence Alignment - The results will be attached as a single alignment on the template sequence.',
    supportingInfo: (
      <>
        <img
          className={css(styles.alignmentDiagram)}
          src={staticURL('/static/img/multisequence_diagram.svg')}
        />
        <ContigMessage />
        <img
          className={css(styles.alignmentDiagram)}
          src={staticURL('/static/img/multisequence_contig_diagram.svg')}
        />
      </>
    ),
    action: {
      actionType: InlineBannerActionType.ACCORDION,
    },
    bannerType: InlineBannerType.INFO,
    hideIcon: true,
  },
  [BulkAlignmentCreationKind.CONSENSUS]: {
    text: 'Consensus Alignment - A new sequence will be created with the consensus of all the selected sequences.',
    supportingInfo: (
      <>
        <img className={css(styles.alignmentDiagram)} src={staticURL('/static/img/consensus_diagram.svg')} />
        <ContigMessage />
        <img
          className={css(styles.alignmentDiagram)}
          src={staticURL('/static/img/consensus_contig_diagram.svg')}
        />
      </>
    ),
    action: {
      actionType: InlineBannerActionType.ACCORDION,
    },
    bannerType: InlineBannerType.INFO,
    hideIcon: true,
  },
};

/**
 * Renders a toggle group for selecting the kind (pairwise, multisequence, consensus) of alignment to perform.
 * @param disabled Boolean indicating if the whole toggle group should be disabled (specifically used for realignments).
 * @param selectedAlignmentKind Currently selected alignment kind.
 * @param dispatch Redux dispatch.
 */
export function AlignmentKindToggleGroup({
  disabled,
  selectedAlignmentKind,
  dispatch,
}: {
  disabled: boolean;
  selectedAlignmentKind: BulkAlignmentCreationKind;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  return (
    <ToggleGroup
      options={[
        {
          id: BulkAlignmentCreationKind.PAIRWISE,
          buttonProps: {text: 'Pairwise'},
          dataTestAttrs: {
            component: 'CreateSequenceAlignmentsModal',
            element: 'alignmentKindToggle',
            key: BulkAlignmentCreationKind.PAIRWISE,
          },
        },
        {
          id: BulkAlignmentCreationKind.MULTISEQUENCE,
          buttonProps: {text: 'Multisequence'},
          dataTestAttrs: {
            component: 'CreateSequenceAlignmentsModal',
            element: 'alignmentKindToggle',
            key: BulkAlignmentCreationKind.MULTISEQUENCE,
          },
        },
        {
          id: BulkAlignmentCreationKind.CONSENSUS,
          buttonProps: {text: 'Consensus'},
          dataTestAttrs: {
            component: 'CreateSequenceAlignmentsModal',
            element: 'alignmentKindToggle',
            key: BulkAlignmentCreationKind.CONSENSUS,
          },
        },
      ]}
      selection={selectedAlignmentKind}
      onChangeSelection={(alignmentType) => {
        dispatch({
          type: CreateSequenceAlignmentModalActionType.SELECT_ALIGNMENT_KIND,
          value: alignmentType,
        });
      }}
      isFullWidth={true}
      size={ButtonSize.MEDIUM}
      disabled={disabled}
    />
  );
}

export interface DefineParametersPaneProps {
  alignmentKind: CreateSequenceAlignmentModalState['alignmentKind'];
  program: CreateSequenceAlignmentModalState['program'];
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
  mafftParameters: CreateSequenceAlignmentModalState['mafftParameters'];
  clustalOParameters: CreateSequenceAlignmentModalState['clustalOParameters'];
  pairwiseConfiguration: CreateSequenceAlignmentModalState['pairwiseConfiguration'];
  multisequenceConfiguration: CreateSequenceAlignmentModalState['multisequenceConfiguration'];
  consensusConfiguration: CreateSequenceAlignmentModalState['consensusConfiguration'];
  inputs: CreateSequenceAlignmentModalState['inputs'];
  folderId: CreateSequenceAlignmentModalState['folderId'];
  disableCircularTemplateRotation: CreateSequenceAlignmentModalState['disableCircularTemplateRotation'];
  realignmentInfo?: CreateSequenceAlignmentModalState['realignmentInfo'];
}

export default function DefineParametersPane({
  alignmentKind,
  program,
  dispatch,
  mafftParameters,
  clustalOParameters,
  pairwiseConfiguration,
  multisequenceConfiguration,
  consensusConfiguration,
  inputs,
  folderId,
  disableCircularTemplateRotation,
  realignmentInfo,
}: DefineParametersPaneProps): JSX.Element {
  const [showParamsSection, setShowParamsSection] = useState(false);

  const isRealign = Boolean(realignmentInfo);

  const allInputs = [...inputs];
  if (realignmentInfo) {
    if (isTemplateSetup(realignmentInfo.sequenceAnalysisInfo.alignmentSetup)) {
      allInputs.push(realignmentInfo.sequenceAnalysisInfo.alignmentSetup.template);
    }
    allInputs.push(...realignmentInfo.sequenceAnalysisInfo.alignmentSetup.items);
  }

  const sortedInputs = naturalSortByKey(allInputs, 'name');

  const {programDisplay, programReferenceLink, strategies} = ALIGNMENT_PROGRAM_DETAILS[program];
  const strategyString =
    program === AlignmentProgram.CLUSTALO
      ? strategies[0].display
      : strategies.find(({value}) => value === mafftParameters.strategy)!.display;

  function getProgramParamsSection(): JSX.Element {
    if (program === AlignmentProgram.MAFFT) {
      return <MafftParametersOptions parameters={mafftParameters} dispatch={dispatch} />;
    } else {
      return <ClustalOParametersOptions parameters={clustalOParameters} dispatch={dispatch} />;
    }
  }

  const shouldShowFolderSection =
    !isRealign &&
    getSelectedSetups(
      alignmentKind,
      pairwiseConfiguration,
      multisequenceConfiguration,
      consensusConfiguration
    ).some((setup) => requiresFolderId(setup));

  const sharedTemplateAlignmentProps = {
    onClose: () => {
      dispatch({
        type: CreateSequenceAlignmentModalActionType.MAYBE_AUTOPOPULATE_TEMPLATE_GROUP,
      });
    },
    allowMulti: !isRealign,
    setDisableCircularTemplateRotation: (value: boolean) => {
      dispatch({
        type: CreateSequenceAlignmentModalActionType.SET_DISABLE_CIRCULAR_TEMPLATE_ROTATION,
        value,
      });
    },
    inputs: sortedInputs,
    alignmentKind,
    circularTemplateRotationDisabled: disableCircularTemplateRotation,
  };

  return (
    <>
      <AlignmentKindToggleGroup
        // Disable changing the alignment kind when a sequence analysis already exists (i.e. realignment).
        disabled={isRealign}
        selectedAlignmentKind={alignmentKind}
        dispatch={dispatch}
      />
      {isRealign && (
        <p className={css(styles.alignmentKindToggleInfoText)}>
          Your realignment must be the same type as your original alignment.
        </p>
      )}
      <div className={css(styles.basicVerticalMargin)}>
        <InlineBanner {...ALIGNMENT_KIND_INLINE_BANNER_PROPS[alignmentKind]} />
      </div>
      <hr />
      {alignmentKind === BulkAlignmentCreationKind.PAIRWISE && (
        <TemplateAlignmentConfiguration
          {...sharedTemplateAlignmentProps}
          addGroup={(template: AlignmentInput) => {
            dispatch({type: CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_TEMPLATE, template});
          }}
          removeGroup={(index: number) => {
            dispatch({type: CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_TEMPLATE, index});
          }}
          addInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_INPUT,
              index,
              input,
            });
          }}
          removeInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_INPUT,
              index,
              input,
            });
          }}
          templateGroups={pairwiseConfiguration}
          allowDuplicateTemplates={false}
        />
      )}
      {alignmentKind === BulkAlignmentCreationKind.MULTISEQUENCE && (
        <TemplateAlignmentConfiguration
          {...sharedTemplateAlignmentProps}
          addGroup={(template: AlignmentInput) => {
            dispatch({type: CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_TEMPLATE, template});
          }}
          removeGroup={(index: number) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_TEMPLATE,
              index,
            });
          }}
          addInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_INPUT,
              index,
              input,
            });
          }}
          removeInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_INPUT,
              index,
              input,
            });
          }}
          templateGroups={multisequenceConfiguration}
          allowDuplicateTemplates={true}
        />
      )}
      {alignmentKind === BulkAlignmentCreationKind.CONSENSUS && (
        <ConsensusAlignmentConfiguration
          addGroup={() => {
            dispatch({type: CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_GROUP});
          }}
          removeGroup={(index: number) => {
            dispatch({type: CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_GROUP, index});
          }}
          allowMulti={!isRealign}
          // Currently, the backend doesn't actually support changing the name of an existing analysis when
          // realigning, so disable the field. Remove this later if we decide to support this on the backend.
          disableRename={isRealign}
          renameGroup={(index: number, name: string) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.SET_CONSENSUS_GROUP_NAME,
              index,
              name,
            });
          }}
          consensusGroups={consensusConfiguration}
          inputs={sortedInputs}
          addInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_INPUT,
              index,
              input,
            });
          }}
          removeInput={(index: number, input: AlignmentInput) => {
            dispatch({
              type: CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_INPUT,
              index,
              input,
            });
          }}
        />
      )}
      {shouldShowFolderSection && (
        <>
          <hr />
          <FolderSection
            folderId={folderId}
            setFolderId={(folderId: string) => {
              dispatch({
                type: CreateSequenceAlignmentModalActionType.SET_FOLDER,
                folderId,
              });
            }}
          />
        </>
      )}
      <hr />
      <div className={css(styles.text, styles.basicVerticalMargin)}>Choose an alignment program.</div>
      {isRealign && (
        <InformationalBanner>
          The fields below are set to the values you chose for your most recent alignment. These values may
          not reflect the selections you made when you last performed this particular alignment.
        </InformationalBanner>
      )}
      <div className={css(styles.buttonGroupBottomSpacing)}>
        {[AlignmentProgram.MAFFT, AlignmentProgram.CLUSTALO].map((programOption, i) => (
          <div
            key={programOption}
            className={className('u-display-inline-block', {
              // Put spacing to the right of all options except the last so that the buttons will look
              // ok regardless of whether or not they are wrapped.
              'u-spacing-right': i === 1,
            })}
          >
            <label
              className={classNames(
                'u-normal-font-weight u-no-wrap',
                css(styles.radioButtonLabel),
                program === programOption
                  ? css(styles.radioButtonSelected)
                  : css(styles.radioButtonUnselected)
              )}
            >
              <input
                checked={program === programOption}
                className={css(styles.input)}
                onChange={() => {
                  dispatch({
                    type: CreateSequenceAlignmentModalActionType.SELECT_PROGRAM,
                    value: programOption,
                  });
                }}
                disabled={false}
                type="radio"
                value={programOption}
                {...getTestProps({
                  component: 'DefineParametersPane',
                  element: 'AlignmentProgramRadioButton',
                  key: programOption,
                })}
              />
              <span className="u-tiny-spacing-left">
                <b>{ALIGNMENT_PROGRAM_DETAILS[programOption].programDisplay}</b>
              </span>
              <div
                className={classNames(css(styles.programRecommendation), css(styles.radioButtonDescription))}
              >
                {ALIGNMENT_PROGRAM_DETAILS[programOption].programRecommendation}
              </div>
              <div className={css(styles.radioButtonDescription)}>
                {ALIGNMENT_PROGRAM_DETAILS[programOption].programDescription}
              </div>
            </label>
          </div>
        ))}
      </div>
      <div className={css(styles.basicVerticalMargin)}>
        <div className={classNames('u-flex-display', css(styles.colPaddingOffset))}>
          <Col lg={7} md={7} sm={7} xs={7}>
            <SelectDropdown
              displayName={`${strategyString} (${programDisplay})`}
              menu={
                <Menu value={program}>
                  {ALIGNMENT_PROGRAM_DETAILS[program].strategies.map((strategy) => (
                    <Option
                      key={strategy.value as string}
                      value={strategy.value}
                      metadata={strategy.description}
                      onSelect={() => {
                        dispatch({
                          type: CreateSequenceAlignmentModalActionType.SELECT_PROGRAM_STRATEGY,
                          value: strategy.value,
                        });
                      }}
                      dataTestAttrs={{
                        component: 'AlignmentProgramOption',
                        element: 'root',
                        key: strategy.value as string,
                      }}
                    >
                      {strategy.display}
                    </Option>
                  ))}
                </Menu>
              }
              dataTestAttrs={{component: 'AlignmentProgramDropdown', element: 'root'}}
            />
          </Col>
          <Col lg={3}>
            <Button
              text={showParamsSection ? 'Hide parameters' : 'Show parameters'}
              className={css(styles.withoutButtonFocusStyling)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={() => {
                setShowParamsSection(!showParamsSection);
              }}
            />
          </Col>
        </div>
      </div>
      {showParamsSection && getProgramParamsSection()}
      <div className={css(styles.referenceText, styles.basicVerticalMargin)}>
        Alignments performed via {programReferenceLink}.
      </div>
    </>
  );
}
